import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Partner {
  id: string;
  name: string;
  logo_url: string;
  slug: string;
}

interface Service {
  id: string;
  name: string;
  price: number;
}

export default function PartnerBooking() {
  const { slug } = useParams<{ slug: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_id: '',
    description: '',
    image_url: ''
  });

  useEffect(() => {
    fetchPartnerData();
  }, [slug]);

  const fetchPartnerData = async () => {
    const { data: pData } = await supabase.from('partners').select('*').eq('slug', slug).single();
    if (pData) {
      setPartner(pData);
      const { data: sData } = await supabase.from('partner_services').select('*').eq('partner_id', pData.id).order('name');
      if (sData) setServices(sData);
    }
    setLoading(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `alterations/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('alterations')
      .upload(filePath, file);

    if (!uploadError) {
      const { data } = supabase.storage.from('alterations').getPublicUrl(filePath);
      setFormData({ ...formData, image_url: data.publicUrl });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedService = services.find(s => s.id === formData.service_id);

    const { error } = await supabase.from('alterations').insert([{
      partner_id: partner?.id,
      client_name: formData.name,
      client_email: formData.email,
      client_phone: formData.phone,
      service_name: selectedService?.name || 'Servicio General',
      price: selectedService?.price || 0,
      description: formData.description,
      image_url: formData.image_url,
      status: 'pending'
    }]);

    if (!error) {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-3xl text-brand-sage" /></div>;
  if (!partner) return <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center"><h1 className="font-serif text-2xl mb-2">Página no encontrada</h1><Link to="/" className="bg-brand-black text-white px-8 py-3 rounded-full text-sm mt-4">Volver al Inicio</Link></div>;

  return (
    <div className="min-h-screen bg-brand-cream/20">
      <Navbar />
      
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white p-8 md:p-12 rounded-[40px] shadow-2xl shadow-brand-sage/10 border border-brand-sage/20"
              >
                <div className="flex flex-col items-center text-center mb-10">
                  <div className="w-16 h-16 rounded-full bg-brand-cream flex items-center justify-center overflow-hidden mb-4 border border-brand-sage/30">
                    {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-cover" /> : <i className="ri-building-line text-brand-sage text-2xl" />}
                  </div>
                  <h2 className="font-serif text-brand-black text-3xl mb-2">Solicitud de Arreglo</h2>
                  <p className="font-sans text-brand-gray-500 text-sm">Servicio exclusivo para clientes de <span className="font-bold text-brand-black">{partner.name}</span></p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Nombre Completo</label>
                    <input
                      type="text" required placeholder="Tu nombre"
                      className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3.5 text-sm focus:bg-white transition-colors"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1">Correo Electrónico</label>
                      <input
                        type="email" required placeholder="tu@email.com"
                        className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3.5 text-sm focus:bg-white transition-colors"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="font-sans text-xs text-brand-gray-600 ml-1">Teléfono de Contacto</label>
                      <input
                        type="tel" required placeholder="+34 000 000 000"
                        className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3.5 text-sm focus:bg-white transition-colors"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Servicio Requerido</label>
                    <select
                      required
                      className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3.5 text-sm focus:bg-white transition-colors appearance-none"
                      value={formData.service_id}
                      onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                    >
                      <option value="">Selecciona un servicio...</option>
                      {services.map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.price}€)</option>
                      ))}
                      <option value="other">Otro (A concretar en Atelier)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Descripción del Arreglo</label>
                    <textarea
                      required rows={4} placeholder="Cuéntanos brevemente qué necesitas..."
                      className="w-full bg-brand-gray-50 border border-brand-gray-100 rounded-2xl px-5 py-3.5 text-sm focus:bg-white transition-colors resize-none"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-sans text-xs text-brand-gray-600 ml-1">Foto de la prenda (Opcional)</label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-brand-sage/30 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-brand-sage/5 transition-colors overflow-hidden relative"
                    >
                      {formData.image_url ? (
                        <img src={formData.image_url} alt="Prenda" className="w-full h-full object-cover" />
                      ) : (
                        <>
                          <i className="ri-image-add-line text-2xl text-brand-sage mb-2" />
                          <p className="text-[10px] text-brand-gray-400 font-sans uppercase tracking-widest">Subir Foto</p>
                        </>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </div>
                  </div>

                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full bg-brand-black text-white py-4 rounded-2xl text-sm font-sans uppercase tracking-[0.2em] font-bold hover:bg-brand-gray-800 transition-all transform active:scale-[0.98] disabled:opacity-50"
                  >
                    {isSubmitting ? 'Procesando...' : 'Solicitar Arreglo'}
                  </button>

                  <p className="text-[10px] text-brand-gray-400 text-center leading-relaxed">
                    Al solicitar este servicio, Anthony Sánchez Couture procesará tus datos para gestionar la cita de acuerdo con nuestra política de privacidad.
                  </p>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white p-12 rounded-[40px] text-center border border-brand-sage/20 shadow-2xl"
              >
                <div className="w-20 h-20 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-4xl text-brand-black" />
                </div>
                <h2 className="font-serif text-3xl mb-4">¡Solicitud Enviada!</h2>
                <p className="font-sans text-brand-gray-500 mb-8 leading-relaxed">
                  Gracias por confiar en Anthony Sánchez Couture. Hemos recibido tu solicitud correctamente y nos pondremos en contacto contigo pronto al teléfono <span className="text-brand-black font-bold">{formData.phone}</span>.
                </p>
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-brand-black text-white px-10 py-4 rounded-full text-xs font-sans uppercase tracking-widest"
                >
                  Nueva Solicitud
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <Footer />
    </div>
  );
}
